import Alpine from "alpinejs";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(CustomEase);

const HERBDORI_CONTENT_POP_EASE = CustomEase.create(
  "herbdoriContentPopEase",
  "M0,0 C0.12,0.72 0.22,1.18 0.42,1.08 C0.54,1.01 0.6,0.965 0.7,0.988 C0.8,1.012 0.9,1.004 1,1"
);

const getPopInner = (element: HTMLElement) =>
  element.querySelector<Element>("[data-herbdori-pop-inner]");

const createPopTimeline = (
  elements: HTMLElement[],
  trigger: HTMLElement,
  stagger = 0
) => {
  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger,
      start: "top 60%",
      once: true,
    },
  });

  elements.forEach((element, index) => {
    const innerElement = getPopInner(element);
    const position = index * stagger;

    timeline
      .to(
        element,
        {
          opacity: 1,
          duration: 0.01,
          ease: "none",
        },
        position
      )
      .to(
        element,
        {
          scale: 1,
          duration: 1.65,
          ease: HERBDORI_CONTENT_POP_EASE,
          onComplete: () => {
            gsap.set(element, { willChange: "auto" });
          },
        },
        position
      );

    if (innerElement) {
      timeline.to(
        innerElement,
        {
          scale: 1,
          duration: 2.3,
          ease: HERBDORI_CONTENT_POP_EASE,
        },
        position + 0.1
      );
    }
  });
};

const addClippedImageAnimation = (
  timeline: gsap.core.Timeline,
  imageFrame: HTMLElement,
  innerImage: HTMLElement,
  position: string | number
) => {
  timeline
    .to(
      imageFrame,
      {
        opacity: 1,
        scale: 1,
        duration: 1.1,
        ease: "back.out(1.4)",
        onComplete: () => {
          gsap.set(imageFrame, { willChange: "auto" });
        },
      },
      position
    )
    .to(
      innerImage,
      {
        scale: 1,
        duration: 2.0,
        ease: "power3.out",
        onComplete: () => {
          gsap.set(innerImage, { willChange: "auto" });
        },
      },
      position
    );
};

export const initHerbdoriContent = () => {
  Alpine.data("herbdoriContent", () => {
    return {
      init() {
        const root = this.$el as HTMLElement;
        const kyushuImage = this.$refs.kyushuImage as HTMLElement | undefined;
        const clippedImageFrames = Array.from(
          root.querySelectorAll<HTMLElement>("[data-herbdori-content-image]")
        );
        const clippedInnerImages = clippedImageFrames
          .map((imageFrame) =>
            imageFrame.querySelector<HTMLElement>(
              "[data-herbdori-content-image-inner]"
            )
          )
          .filter((image): image is HTMLElement => image instanceof HTMLElement);
        const popGroups = Array.from(
          root.querySelectorAll<HTMLElement>("[data-herbdori-pop-group]")
        );
        const popElements = Array.from(
          root.querySelectorAll<HTMLElement>("[data-herbdori-pop-image]")
        );
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        if (
          !kyushuImage &&
          clippedImageFrames.length === 0 &&
          popElements.length === 0
        ) {
          return;
        }

        if (kyushuImage) {
          gsap.set(kyushuImage, {
            opacity: 0,
            yPercent: 5,
            transformOrigin: "right bottom",
          });
        }
        gsap.set(clippedImageFrames, {
          opacity: 0,
          scale: 0.9,
          transformOrigin: "50% 50%",
          force3D: true,
          willChange: "transform, opacity",
        });
        gsap.set(clippedInnerImages, {
          scale: 1.05,
          transformOrigin: "center center",
        });
        popElements.forEach((popElement) => {
          const innerElement = getPopInner(popElement);

          gsap.set(popElement, {
            opacity: 0,
            scale: 0.82,
            transformOrigin: "50% 50%",
            force3D: true,
            willChange: "transform, opacity",
          });

          if (innerElement) {
            const svgOrigin = innerElement.getAttribute("data-herbdori-pop-origin");
            gsap.set(innerElement, {
              scale: 1.18,
              ...(svgOrigin ? { svgOrigin } : { transformOrigin: "50% 50%" }),
            });
          }
        });

        if (prefersReducedMotion) {
          if (kyushuImage) {
            gsap.set(kyushuImage, { opacity: 1, yPercent: 0 });
          }
          gsap.set(clippedImageFrames, {
            opacity: 1,
            scale: 1,
            willChange: "auto",
          });
          gsap.set(clippedInnerImages, {
            scale: 1,
          });
          popElements.forEach((popElement) => {
            gsap.set(popElement, { opacity: 1, scale: 1 });
            const innerElement = getPopInner(popElement);
            if (innerElement) {
              gsap.set(innerElement, { scale: 1 });
            }
          });
          return;
        }

        if (kyushuImage) {
          gsap.to(kyushuImage, {
            opacity: 1,
            yPercent: 0,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: kyushuImage,
              start: "top 40%",
              once: true,
            },
          });
        }

        clippedImageFrames.forEach((imageFrame) => {
          const innerImage = imageFrame.querySelector<HTMLElement>(
            "[data-herbdori-content-image-inner]"
          );

          if (!innerImage) {
            return;
          }

          const clippedImageTl = gsap.timeline({
            scrollTrigger: {
              trigger: imageFrame,
              start: "top 70%",
              once: true,
            },
          });

          addClippedImageAnimation(clippedImageTl, imageFrame, innerImage, "0");
        });

        const groupedPopElements = new Set<HTMLElement>();
        popGroups.forEach((group) => {
          const groupElements = Array.from(
            group.querySelectorAll<HTMLElement>("[data-herbdori-pop-image]")
          );

          groupElements.forEach((element) => groupedPopElements.add(element));

          if (groupElements.length > 0) {
            createPopTimeline(groupElements, group, 0.14);
          }
        });

        popElements
          .filter((element) => !groupedPopElements.has(element))
          .forEach((element) => {
            createPopTimeline([element], element);
          });
      },
    };
  });
};
